import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { GraduationCap, Info } from '@phosphor-icons/react';
import api, { getErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const AISettings = () => {
  const [globalTraining, setGlobalTraining] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  useEffect(() => {
    fetchGlobalTraining();
  }, []);

  const fetchGlobalTraining = async () => {
    try {
      const response = await api.get('/ai-settings/global');
      setGlobalTraining(response.data.content || '');
      setLastUpdated(response.data.updated_at || null);
      setUpdatedBy(response.data.updated_by || null);
      setLoading(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load training content'));
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/ai-settings/global', { content: globalTraining });
      toast.success('Global training content updated successfully!');
      // Refresh to get updated timestamp
      await fetchGlobalTraining();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save training content'));
    } finally {
      setSaving(false);
    }
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return null;
    
    try {
      const date = new Date(lastUpdated);
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return date.toLocaleDateString('en-US', options);
    } catch {
      return lastUpdated;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C4C3B]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GraduationCap size={32} className="text-[#C05C42]" />
            <div>
              <h1 className="text-3xl font-light text-[#2C4C3B]">AI Guide Training</h1>
              <p className="text-sm text-[#1C1C1A]/60">Customize how AI generates staff guides</p>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <Card className="p-4 bg-[#FDF5E6] border-[#C05C42]/20">
          <div className="flex items-start gap-3">
            <Info size={24} className="text-[#C05C42] flex-shrink-0 mt-1" />
            <div className="text-sm text-[#1C1C1A]/80">
              <p className="font-medium mb-1">How Training Content Works:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Global Training</strong> applies to ALL branches and guides</li>
                <li>Each branch can add <strong>Branch-Specific Instructions</strong> that supplement the global training</li>
                <li>When generating an AI guide, both global + branch instructions are used</li>
                <li>Staff guides will follow the patterns and philosophy you define here</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Global Training Content */}
        <Card className="p-6">
          <div className="space-y-4">
            <div>
              <Label className="text-lg font-medium text-[#2C4C3B]">
                Global Training Content
              </Label>
              <p className="text-sm text-[#1C1C1A]/60 mt-1">
                These instructions apply to AI guide generation across all branches
              </p>
            </div>

            <div>
              <Textarea
                value={globalTraining}
                onChange={(e) => setGlobalTraining(e.target.value)}
                placeholder="Example: Always greet customer by name. Ask about health concern first. Never discuss price directly. Focus on experience..."
                className="min-h-[400px] font-mono text-sm"
              />
              <p className="text-xs text-[#1C1C1A]/60 mt-2">
                Tip: Include greeting style, key talking points, product benefits, objection handling, and session note guidelines
              </p>
            </div>

            <div className="pt-4 border-t space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#1C1C1A]/60">
                  {globalTraining.length} characters
                </p>
                <Button
                  onClick={handleSave}
                  disabled={saving || !globalTraining.trim()}
                  className="bg-[#2C4C3B] hover:bg-[#2C4C3B]/90"
                >
                  {saving ? 'Saving...' : 'Save Global Training'}
                </Button>
              </div>
              
              {lastUpdated && (
                <p className="text-sm text-[#1C1C1A]/60 italic">
                  Last updated: {formatLastUpdated()} by {updatedBy || 'system'}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Preview Section */}
        <Card className="p-6 bg-[#F9F9F8]">
          <div className="space-y-3">
            <h3 className="font-medium text-[#2C4C3B]">How AI Will Use This Content</h3>
            <div className="text-sm text-[#1C1C1A]/70 space-y-2">
              <p>When generating a staff guide for a customer, the AI will:</p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Read the global training instructions (what you entered above)</li>
                <li>Read any branch-specific instructions (set by branch owner)</li>
                <li>Review customer profile, health concerns, and past session notes</li>
                <li>Generate a personalized guide following your training patterns</li>
              </ol>
              <p className="mt-3 text-xs italic">
                Example: If you write &quot;Always ask about sleep quality&quot;, every guide will include questions about the customer&apos;s sleep.
              </p>
            </div>
          </div>
        </Card>

        {/* Branch Instructions Info */}
        <Card className="p-4 border-[#2C4C3B]/20">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-[#2C4C3B] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-[#1C1C1A]/70">
              <p>
                <strong>Branch owners</strong> can add their own training instructions in <strong>Branch Settings → AI Training</strong>.
                Their instructions will be added to the global training when generating guides for their branch.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default AISettings;
